import { useMemo } from "react";
import { useGetAllPortfoliosHoldings } from "api/holdings/useGetAllPortfoliosHoldings";
import { useGetSecurities } from "api/securities/useGetSecurities";
import { HOLDINGS_SETTINGS, LogoOption, VALID_ASSET_TYPES } from "../types";
import { useIssuerDirectory } from "./useIssuerDirectory";

interface UseRelevantIssuersProps {
  holdingsSetting: HOLDINGS_SETTINGS;
  assetTypeSetting: VALID_ASSET_TYPES | null;
}

/** Asset type code -> the portal-side issuer ids found under it. */
type IdsByAssetType = Map<string, Set<string>>;

/**
 * Buckets grow to fit whatever FA sends. This used to be pre-seeded from
 * `VALID_ASSET_TYPES` and gated with `map.has(code)`, which made the enum an
 * allowlist: any security whose sector code was not one of the six literals
 * was dropped from *every* bucket, the unfiltered one included. That turned a
 * single wrong constant (`3ALTINV` for what FA actually calls `ALTER`) into a
 * silently empty picker rather than a visible error, and it would have hidden
 * `CASH` funds too. Relevance is decided by the CMS join further down, so
 * there is nothing for a code-level allowlist to protect here.
 */
const addIssuerId = (map: IdsByAssetType, code: string, issuerId: string) => {
  const bucket = map.get(code);
  if (bucket) bucket.add(issuerId);
  else map.set(code, new Set([issuerId]));
};

/**
 * Which issuers are worth contacting, given what the user holds (or the whole
 * fund universe) and an asset-type filter.
 *
 * This hook owns *relevance* only: it walks portal holdings/securities to
 * collect issuer ids per asset type, then inner-joins those ids against
 * `useIssuerDirectory` for name and logo. Consequences of that split:
 *
 * - Identity (name, logo) comes from one swappable place, never from the
 *   portal's `class2Name` or a hardcoded logo map.
 * - Presence in the directory *is* the "can be contacted" gate, so an issuer
 *   you hold that the directory doesn't list is simply not offered. That
 *   replaces the old partner check against the portal's `groups` query.
 */
export const useRelevantIssuers = ({
  holdingsSetting,
  assetTypeSetting,
}: UseRelevantIssuersProps) => {
  const {
    data: portfolioHoldingsData,
    loading: portfolioHoldingsLoading,
    error: portfolioHoldingsError,
  } = useGetAllPortfoliosHoldings();

  const {
    data: allSecuritiesData,
    error: allSecuritiesError,
    loading: allSecuritiesLoading,
  } = useGetSecurities();

  const {
    data: directory,
    loading: directoryLoading,
    error: directoryError,
  } = useIssuerDirectory();

  const idsByAssetType = useMemo<IdsByAssetType>(() => {
    const map: IdsByAssetType = new Map();

    if (holdingsSetting === HOLDINGS_SETTINGS.PORTFOLIO) {
      portfolioHoldingsData?.allocationByType
        .flatMap((allocation) => allocation.allocationsBySector)
        .forEach((allocation) => {
          allocation.allocationsBySecurity.forEach(({ security }) => {
            const code = security.class2Code;
            if (code) addIssuerId(map, allocation.code, String(code));
          });
        });
    } else if (holdingsSetting === HOLDINGS_SETTINGS.ALL) {
      allSecuritiesData?.forEach((security) => {
        if (!security.issuer) return;
        // A mixed fund sits under several asset types at once, so it makes its
        // issuer contactable about each of them — not just the first one FA
        // happens to list.
        security.sectorCodes.forEach((code) =>
          addIssuerId(map, code, String(security.issuer.id))
        );
      });
    }

    return map;
  }, [holdingsSetting, portfolioHoldingsData, allSecuritiesData]);

  const data = useMemo<LogoOption[]>(() => {
    const relevantIds =
      assetTypeSetting && assetTypeSetting !== VALID_ASSET_TYPES.ALL
        ? idsByAssetType.get(assetTypeSetting) ?? new Set<string>()
        : new Set(
            Array.from(idsByAssetType.values()).flatMap((ids) =>
              Array.from(ids)
            )
          );

    return directory
      .filter((entry) => relevantIds.has(entry.externalId))
      .map((entry) => ({
        id: entry.externalId,
        name: entry.name,
        logoUrl: entry.logoUrl,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "sv"));
  }, [assetTypeSetting, idsByAssetType, directory]);

  return {
    data,
    loading:
      directoryLoading ||
      (holdingsSetting === HOLDINGS_SETTINGS.PORTFOLIO
        ? portfolioHoldingsLoading
        : allSecuritiesLoading),
    error: directoryError || allSecuritiesError || portfolioHoldingsError,
  };
};
